"use client";

import { motion } from "motion/react";
import {
  SpellCheck,
  Sparkles,
  Languages,
  PenLine,
  SlidersHorizontal,
  LayoutTemplate,
} from "lucide-react";
import { useLang } from "@/context/lang";
import { useUpper } from "@/lib/upper";

const featureIcons = [SpellCheck, Sparkles, Languages, PenLine, SlidersHorizontal, LayoutTemplate];
const featureKeys = ["creaneo.f1", "creaneo.f2", "creaneo.f3", "creaneo.f4", "creaneo.f5", "creaneo.f6"];

export function CreaneoAI() {
  const { T } = useLang();
  const U = useUpper();

  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-900">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, type: "spring", stiffness: 200 }}
            className="inline-block text-sm font-bold text-cyan-600 dark:text-cyan-300 tracking-wider bg-cyan-50 dark:bg-cyan-500/15 px-4 py-1.5 rounded-full border border-cyan-100 dark:border-cyan-400/30 mb-4"
          >
            {U(T("creaneo.badge"))}
          </motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 dark:text-white mt-4">
            <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">
              CREANEO AI
            </span>
          </h2>
          <p className="mt-4 text-lg text-neutral-600 dark:text-slate-300 max-w-2xl mx-auto">
            {T("creaneo.desc")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featureKeys.map((key, i) => {
            const Icon = featureIcons[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.6, type: "spring", stiffness: 100 }}
                whileHover={{ y: -8, transition: { type: "spring", stiffness: 300, damping: 20 } }}
                className="group relative p-6 rounded-2xl border border-neutral-200 dark:border-white/10 hover:border-cyan-200 dark:hover:border-cyan-400/40 hover:shadow-xl hover:shadow-cyan-500/10 transition-all duration-300 bg-white dark:bg-slate-900"
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-50/0 dark:from-cyan-500/0 to-teal-50/0 group-hover:from-cyan-50/50 group-hover:to-teal-50/50 dark:group-hover:from-cyan-500/10 dark:group-hover:to-teal-500/10 transition-all duration-300" />

                <div className="relative z-10">
                  <motion.div
                    whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                    className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-500/15 dark:to-teal-500/10 border border-cyan-100 dark:border-cyan-400/30 flex items-center justify-center mb-5 group-hover:from-cyan-100 group-hover:to-teal-100 dark:group-hover:from-cyan-500/25 dark:group-hover:to-teal-500/20 group-hover:border-cyan-200 dark:hover:border-cyan-400/40 transition-colors duration-300"
                  >
                    <Icon size={22} className="text-cyan-600 dark:text-cyan-300" />
                  </motion.div>
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-white mb-2">
                    {T(`${key}.title`)}
                  </h3>
                  <p className="text-sm text-neutral-600 dark:text-slate-300 leading-relaxed">
                    {T(`${key}.desc`)}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
